import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';

import { Stack } from '@mui/material';

import React, { useContext, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { ListHookResult } from '_lib/api/apiResourceFactory';
import {
  PointmanCustomerCreateDTO,
  PointmanCustomerCreateSchema,
  PointmanCustomerDetailDTO,
  PointmanCustomerListDTO
} from '_lib/api/apiTypes.shim';
import { WarningAlert } from '_lib/components/Alert/Alert';
import { Avatar } from '_lib/components/Avatar/Avatar';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorBoundaryFallback } from '_lib/components/ErrorBoundaryFallback/ErrorBoundaryFallback';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { ListItemAvatar } from '_lib/components/ListItemAvatar/ListItemAvatar';
import { ListItemButton } from '_lib/components/ListItemButton/ListItemButton';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { MaskedPhoneDisplay } from '_lib/components/MaskedPhoneDisplay/MaskedPhoneDisplay';
import { Search } from '_lib/components/Search/Search';
import { TabPanel } from '_lib/components/TabPanel/TabPanel';
import { Typography } from '_lib/components/Typography/Typography';
import { PagedList } from '_lib/featureComponents/PagedList/PagedList';
import {
  PrimaryHeader,
  PrimaryHeaderIconButton,
  StyledDivider,
  StyledTab,
  StyledTabs
} from '_lib/styles/PhoneCall.styled';
import { PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { formatAddressOneLine, formatPhone } from '_lib/utils/helpers';

import { apiSubmitHandler } from '_lib/utils/hookFormUtils';

import { CustomerForm } from '../CustomerForm/CustomerForm';

type CustomerSelectionProps = {
  callerId: PhoneCallURLState['callerId'];
  companyId: string;
  customerQuery: ListHookResult<PointmanCustomerListDTO>;
  customerSearchWords?: string;
  setCustomerSearchWords: (value: React.SetStateAction<string | undefined>) => void;
  onSetCustomer: (customer: PointmanCustomerDetailDTO) => void;
};

export const CustomerSelection = (props: CustomerSelectionProps) => {
  const { callerId, companyId, customerQuery, customerSearchWords, setCustomerSearchWords, onSetCustomer } = props;

  const api = useContext(ApiContext);

  const customerPostMutation = api.pointmanCustomers.useCreate();

  const [tabIndex, setTabIndex] = useState(0);

  const scrollRef = useRef<HTMLDivElement>(undefined);

  const [validationErrors, setValidationErrors] = useState<string[]>();

  const [detailIsFetching, setDetailIsFetching] = useState(false);

  const customerCreateFormContext = useForm<PointmanCustomerCreateDTO>({
    mode: 'onChange',
    shouldFocusError: true,
    resolver: zodResolver(PointmanCustomerCreateSchema),
    defaultValues: { isCommercial: false, phoneNumber: callerId ?? undefined, isOwner: true, companyId }
  });

  const handleSave = apiSubmitHandler(
    customerCreateFormContext.handleSubmit,
    customerCreateFormContext.setError,
    async (data, { setFormErrors }) => {
      setValidationErrors(undefined);
      try {
        const createdCustomer = await customerPostMutation.create({ data });
        onSetCustomer(createdCustomer);
      } catch (err) {
        if (err instanceof BadRequestError) {
          setValidationErrors(err.responseData?.nonFieldErrors);
          setFormErrors(err);
        }
      }
    }
  );

  return (
    <>
      <PrimaryHeader sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <FlexBox sx={{ flexDirection: 'row', justifyContent: 'space-between', paddingX: 1.5, paddingY: 1 }}>
          <Stack useFlexGap sx={{ alignItems: 'flex-start' }}>
            <Typography variant='h3' text='Unknown' />
            <MaskedPhoneDisplay variant='h3' preText='CID: ' text={callerId ?? 'Invalid Caller ID'} />
          </Stack>
          {tabIndex === 1 && (
            <Stack direction='row' sx={{ justifyContent: 'flex-end', alignItems: 'baseline' }}>
              <PrimaryHeaderIconButton
                onClick={() => {
                  handleSave();
                }}
                disabled={customerPostMutation.isPending}>
                <SaveIcon />
              </PrimaryHeaderIconButton>
              <PrimaryHeaderIconButton onClick={() => setTabIndex(0)} disabled={customerPostMutation.isPending}>
                <CloseIcon />
              </PrimaryHeaderIconButton>
            </Stack>
          )}
        </FlexBox>
        <StyledDivider />
        <StyledTabs
          value={tabIndex}
          onChange={(_event: React.SyntheticEvent, newValue: number) => setTabIndex(newValue)}>
          <StyledTab label='SEARCH' />
          <StyledTab label='NEW CUSTOMER' />
        </StyledTabs>
      </PrimaryHeader>
      <TabPanel currentIndex={tabIndex} index={0} sx={{ overflow: 'hidden' }}>
        <Stack spacing={0} useFlexGap sx={{ width: '100%' }}>
          {!!(!customerSearchWords || customerSearchWords?.length < 2) && (
            <WarningAlert>
              <Typography>Please enter at least 2 characters to search.</Typography>
            </WarningAlert>
          )}
          <Search
            placeholder='Search for a customer...'
            initialValue={customerSearchWords}
            millis={500}
            onSearch={(searchWords) => {
              setCustomerSearchWords(searchWords);
              scrollRef.current?.scrollTo(0, 0);
            }}
            sx={{ width: '100%', padding: 2, margin: 0 }}
          />
          <Divider />
          <FlexBox
            sx={(theme) => ({
              backgroundColor: theme.palette.background.default,
              position: 'relative',
              flexDirection: 'column',
              overflow: customerQuery.isFetching ? 'none' : 'auto',
              flexGrow: 1,
              width: '100%'
            })}
            ref={scrollRef}>
            <BackdropSpinner
              open={customerQuery.isFetching || detailIsFetching}
              sx={{ alignItems: 'flex-start', paddingTop: 16 }}
            />
            {!customerQuery.error && !!(customerSearchWords && customerSearchWords.length >= 2) && (
              <PagedList
                queryResult={customerQuery}
                renderFunction={(customer) => (
                  <ListItemButton
                    disabled={detailIsFetching}
                    key={customer.id}
                    onClick={async () => {
                      setDetailIsFetching(true);
                      try {
                        const customerDetail = await api.pointmanCustomers.getById({
                          id: customer.id,
                          params: { companyId: companyId }
                        });
                        onSetCustomer(customerDetail);
                      } finally {
                        setDetailIsFetching(false);
                      }
                    }}>
                    <ListItemAvatar>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack spacing={0}>
                          <Typography
                            variant='body2'
                            text={
                              (customer.isCommercial ? customer.companyName : customer.fullName) ?? 'Unknown Customer'
                            }
                          />
                          <Typography variant='body2' text={formatPhone(customer.phoneNumber)} />
                        </Stack>
                      }
                      secondary={formatAddressOneLine(customer)}
                    />
                  </ListItemButton>
                )}
              />
            )}
            {customerQuery.error && (
              <ErrorBoundaryFallback
                onRefresh={() => {
                  customerQuery.refetch();
                }}
                title={'Error Searching Customers'}
                sx={{ justifyContent: 'flex-start', paddingTop: 8 }}
              />
            )}
          </FlexBox>
        </Stack>
      </TabPanel>
      <TabPanel currentIndex={tabIndex} index={1} sx={{ overflow: 'auto' }}>
        <FlexBox
          sx={(theme) => ({
            backgroundColor: theme.palette.background.default,
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            position: 'relative'
          })}>
          <BackdropSpinner open={customerPostMutation.isPending} />
          {validationErrors?.length && <ErrorDisplay errors={validationErrors} />}
          <CustomerForm customerFormContext={customerCreateFormContext} />
        </FlexBox>
      </TabPanel>
    </>
  );
};
